import React, { useEffect } from 'react';

import { ErrorCodes } from '@aline/common';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { useTheme } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useLatest } from 'react-use';
import { BaseSnackbar, SelectButton } from 'src/components';
import { fakeNotistackMessage } from 'src/helpers/snackbars';
import { v4 as uuid4 } from 'uuid';

export interface SkillSaveError {
  message: string;
  /** The save was based on a version someone has since replaced; retrying cannot succeed. */
  outdated: boolean;
}

const RETRYABLE_SNACK_MS = 10000;

/** Whether a failed save was refused because the skill changed since the editor loaded it. */
export function getIsOutdatedSkillVersionError(error: unknown): boolean {
  return (
    CombinedGraphQLErrors.is(error) &&
    error.errors.some(
      graphQLError => graphQLError.extensions?.code === ErrorCodes.OUTDATED_SKILL_VERSION
    )
  );
}

/**
 * Shows why the last save failed as a toast, with the way out: Retry for an error that may
 * pass, Reload when someone else saved a newer version, which no retry can overwrite. The
 * conflict toast stays until the error clears, since the editor is locked until Reload.
 */
export function useSkillSaveErrorSnack({
  error,
  onReload,
  onRetry,
  reloading,
}: {
  error: SkillSaveError | null;
  onReload: () => void;
  onRetry: () => void;
  reloading: boolean;
}) {
  const theme = useTheme();
  const { closeSnackbar, enqueueSnackbar } = useSnackbar();
  const latestRef = useLatest({ onReload, onRetry, reloading });

  useEffect(() => {
    if (!error) return;
    const key = uuid4();
    const handleAction = () => {
      const latest = latestRef.current;
      if (!error.outdated) latest.onRetry();
      else if (!latest.reloading) latest.onReload();
    };
    const message = error.outdated
      ? 'Someone saved a newer version of this skill, so your changes cannot be saved. Reload to open it. This discards your changes here, so copy anything you want to keep first.'
      : `${error.message} Your changes have not been saved yet.`;
    enqueueSnackbar(fakeNotistackMessage, {
      autoHideDuration: RETRYABLE_SNACK_MS,
      content: (
        <BaseSnackbar
          action={
            <SelectButton style={{ color: theme.palette.primary.main }} onClick={handleAction}>
              {error.outdated ? 'Reload' : 'Retry'}
            </SelectButton>
          }
          id={key}
          message={message}
          variant={error.outdated ? 'warning' : 'error'}
        />
      ),
      key,
      persist: error.outdated,
    });
    return () => closeSnackbar(key);
  }, [closeSnackbar, enqueueSnackbar, error, latestRef, theme]);
}
